import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import { api } from "../lib/api";

const CATEGORY_COLOR = {
  whisky: "#8b5a2b", rum: "#a67034", tequila: "#b8a663", mezcal: "#7a5d3a",
  vodka: "#9ec0d8", liqueurs: "#b5667d", agave: "#a4b35a", aperitifs: "#c75a3a",
  wine: "#7a2434", beer: "#c79133", sake: "#d4d2c4", vermouth: "#8b3b3b", amaro: "#5e2a2a",
};

const KNOWN_CATEGORIES = new Set([
  "gin", "whisky", "rum", "tequila", "mezcal", "vodka", "liqueurs", "agave",
  "aperitifs", "wine", "beer", "sake", "vermouth", "amaro",
]);

export default function SpiritsCategory() {
  const { category } = useParams();
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const [priceMax, setPriceMax] = useState(0);
  const [loading, setLoading] = useState(true);

  const isGin = category === "gin";
  const isUnknown = !KNOWN_CATEGORIES.has(category);

  useEffect(() => {
    if (isGin || isUnknown) return;
    setLoading(true);
    Promise.all([
      api.get(`/beverages?category=${category}&limit=500`),
      api.get(`/beverages/categories`),
    ])
      .then(([listRes, catRes]) => {
        setItems(listRes.data.beverages || []);
        const m = (catRes.data.categories || []).find((c) => c.id === category);
        setMeta(m || null);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [category, isGin, isUnknown]);

  const maxPrice = useMemo(() => items.reduce((m, b) => Math.max(m, b.price || 0), 0), [items]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((b) => {
      if (term) {
        const hay = `${b.display_name} ${b.brand} ${b.expression || ""} ${b.country || ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      if (priceMax > 0 && (b.price || 0) > priceMax) return false;
      return true;
    });
  }, [items, q, priceMax]);

  // Gin is the flagship — redirect into the deep atlas
  if (isGin) return <Navigate to="/gin/explore" replace />;
  if (isUnknown) return <Navigate to="/spirits" replace />;

  const color = CATEGORY_COLOR[category] || "#888";

  return (
    <div className="bg-[#0E0F0C] text-white min-h-screen" data-testid={`spirits-category-${category}`}>
      <section className="px-5 md:px-12 py-12 md:py-14 max-w-7xl mx-auto">
        <Link to="/spirits" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-[#C17F59] transition-colors mb-6" data-testid="back-spirits">
          <ArrowLeft size={14} /> All spirits
        </Link>
        <div className="flex items-center gap-4 mb-3">
          <div className="w-3 h-3 rounded-full" style={{ background: color }} />
          <p className="overline text-white/55">Category</p>
        </div>
        <h1 className="font-display text-5xl sm:text-6xl mb-3 capitalize" data-testid="spirits-category-title">{meta?.label || category}</h1>
        {meta?.description && (
          <p className="text-white/65 max-w-2xl leading-relaxed">{meta.description}</p>
        )}
        <p className="font-hand text-xl text-[#D89A74] mt-4 -rotate-1">
          ~ {items.length} bottle{items.length === 1 ? "" : "s"} on the shelf
        </p>
      </section>

      {/* Empty state */}
      {!loading && items.length === 0 && (
        <section className="px-5 md:px-12 max-w-3xl mx-auto pb-24" data-testid="spirits-empty-state">
          <div className="bg-[#15171A] border border-white/10 p-8 md:p-12 text-center">
            <p className="overline text-[#C17F59] mb-3">Shelf is bare here</p>
            <h3 className="font-display text-3xl mb-3">No {meta?.label || category} on the menu yet.</h3>
            <p className="text-white/55 leading-relaxed max-w-md mx-auto">
              The bartender hasn't uploaded {meta?.label?.toLowerCase() || category} bottles yet. Come back soon — or browse a different category.
            </p>
            <div className="flex gap-3 justify-center mt-6">
              <Link to="/spirits" className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-xs uppercase tracking-[0.25em] hover:border-[#C17F59] hover:text-[#C17F59] transition-colors" data-testid="empty-state-back">
                Browse other categories
              </Link>
              <Link to="/gin" className="inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-5 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#D89A74] transition-colors" data-testid="empty-state-gin">
                Try the Gin Atlas
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Filters + list */}
      {items.length > 0 && (
        <>
          <section className="px-5 md:px-12 max-w-7xl mx-auto pb-6">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px] relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="text" value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder={`Search ${meta?.label?.toLowerCase() || "bottles"}...`}
                  className="w-full bg-transparent border border-white/15 focus:border-[#C17F59] focus:outline-none text-sm pl-9 pr-3 py-3"
                  data-testid="spirits-search"
                />
              </div>
              {maxPrice > 0 && (
                <div className="flex items-center gap-3">
                  <label className="overline text-white/40">Max price</label>
                  <input
                    type="range" min={0} max={Math.ceil(maxPrice / 100) * 100} step={50}
                    value={priceMax} onChange={(e) => setPriceMax(Number(e.target.value))}
                    className="w-40 accent-[#C17F59]" data-testid="spirits-price-range"
                  />
                  <span className="text-xs text-white/65 min-w-[60px]">
                    {priceMax === 0 ? "All" : `≤ ₹${priceMax}`}
                  </span>
                </div>
              )}
            </div>
            <p className="overline text-white/40 mt-3" data-testid="spirits-results-count">
              {filtered.length} of {items.length} bottle{items.length === 1 ? "" : "s"}
            </p>
          </section>

          <section className="px-5 md:px-12 max-w-7xl mx-auto pb-24">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((b) => (
                <Link key={b.id} to={`/spirits/item/${b.id}`}
                      className={`group relative bg-[#15171A] border p-5 transition-colors ${b.is_available ? "border-white/8 hover:border-white/25" : "border-white/5"}`}
                      data-testid={`spirits-card-${b.id}`}
                      style={b.is_available ? {} : { opacity: 0.6, filter: "saturate(0.35)" }}
                >
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <p className="overline text-white/40">{b.brand}</p>
                    {!b.is_available && (
                      <span className="text-[9px] uppercase tracking-[0.2em] text-white/50 border border-white/20 px-2 py-[2px]" data-testid="not-available-badge">
                        Not on menu
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-2xl leading-tight mb-2">{b.display_name}</h3>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/55">
                    {b.country && <span>{b.country}</span>}
                    {b.abv && <span>{b.abv}% ABV</span>}
                    {b.subcategory && <span className="capitalize">{b.subcategory}</span>}
                  </div>
                  {b.notes && <p className="text-xs text-white/50 mt-3 line-clamp-2">{b.notes}</p>}
                  <div className="flex items-center justify-between pt-4 mt-4 border-t border-white/5">
                    <PourLabel b={b} />
                    <PriceTag b={b} />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function PourLabel({ b }) {
  const mode = b.pricing_mode || "POUR_ONLY";
  if (mode === "BOTTLE_ONLY") return <p className="overline text-[#C17F59]" data-testid="pour-label-bottle-only">Bottle only</p>;
  if (mode === "BOTH") return <p className="overline text-white/40">{b.standard_pour_ml || 30}ml · bottle</p>;
  return <p className="overline text-white/40">{b.standard_pour_ml || 30}ml</p>;
}

function PriceTag({ b }) {
  const mode = b.pricing_mode || "POUR_ONLY";
  if (mode === "BOTTLE_ONLY") {
    return <p className="font-display text-xl" data-testid="price-bottle">₹{b.price_by_bottle || "—"}</p>;
  }
  if (mode === "BOTH") {
    return (
      <div className="text-right" data-testid="price-both">
        <p className="font-display text-xl leading-none">₹{b.price_by_glass || "—"}</p>
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">Btl ₹{b.price_by_bottle}</p>
      </div>
    );
  }
  return <p className="font-display text-xl" data-testid="price-pour">₹{b.price_by_glass || b.price || "—"}</p>;
}
